import React, { useEffect, useMemo, useState } from 'react';
import { Pencil1Icon, ChevronUpIcon, ChevronDownIcon, TrashIcon } from '@radix-ui/react-icons';
import { useNavigate } from 'react-router-dom';

import API_BASE from '../../utils/api.js';
import usePermissions from '../../auth/usePermissions';

/* =========================================================
   SORT ARROW
========================================================= */
function SortArrow({ active, dir }) {
  if (!active) {
    return (
      <span style={{ marginLeft: 6, opacity: 0.35 }}>
        <ChevronUpIcon />
      </span>
    );
  }

  return (
    <span style={{ marginLeft: 6 }}>{dir === 'asc' ? <ChevronUpIcon /> : <ChevronDownIcon />}</span>
  );
}

export default function TimeBillingTable({ projectId }) {
  /* ================= STATE ================= */
  const [entries, setEntries] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [taxLocations, setTaxLocations] = useState([]);
  const [selected, setSelected] = useState({});
  const [view, setView] = useState('entries');
  const [project, setProject] = useState(null);

  const [entrySort, setEntrySort] = useState({ key: 'date', dir: 'desc' });
  const [invoiceSort, setInvoiceSort] = useState({
    key: 'invoiceNumber',
    dir: 'asc',
  });

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteContext, setDeleteContext] = useState(null);

  const [billableFilter, setBillableFilter] = useState('all');
  const [showAddToInvoiceModal, setShowAddToInvoiceModal] = useState(false);
  const [addToInvoiceLoading, setAddToInvoiceLoading] = useState(false);

  /* ================= ROUTING ================= */
  const navigate = useNavigate();

  const { permissions } = usePermissions();
  const canEditEntries = permissions.editTeamTimeEntries !== false;
  const canCreateTBInvoices = permissions.createTimeBillingInvoices !== false;
  const canSelectRows = canEditEntries || canCreateTBInvoices;

  const userId = localStorage.getItem('userId');
  const apiUrl = API_BASE;

  /* ================= TAX ================= */
  const parseTaxRate = (rawValue) => {
    if (rawValue == null || rawValue === '') return 0;

    if (typeof rawValue === 'string') {
      const trimmed = rawValue.trim();
      if (!trimmed) return 0;

      if (trimmed.includes('%')) {
        const n = parseFloat(trimmed.replace('%', ''));
        return Number.isFinite(n) ? n / 100 : 0;
      }

      const n = Number(trimmed);
      if (!Number.isFinite(n)) return 0;
      return n > 1 ? n / 100 : n;
    }

    const n = Number(rawValue);
    if (!Number.isFinite(n)) return 0;
    return n > 1 ? n / 100 : n;
  };

  const taxRate = useMemo(() => {
    const projectTax = parseTaxRate(project?.taxLocation?.tax);
    if (projectTax > 0) return projectTax;

    const salesTaxRateId = project?.salesTaxRate;
    if (!salesTaxRateId) return 0;

    const matchedTaxLocation = taxLocations.find(
      (loc) => String(loc?.id || '') === String(salesTaxRateId)
    );
    if (matchedTaxLocation) {
      return parseTaxRate(matchedTaxLocation.tax ?? matchedTaxLocation.rate);
    }

    return parseTaxRate(salesTaxRateId);
  }, [project?.taxLocation?.tax, project?.salesTaxRate, taxLocations]);
  /* =========================================================
     FETCH DATA
  ========================================================= */
  const fetchEntries = async () => {
    if (!userId || !projectId) return;

    const res = await fetch(
      `${apiUrl}/api/timebilling/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`
    );
    const data = await res.json();
    setEntries(Array.isArray(data) ? data : []);
  };

  const fetchInvoices = async () => {
    if (!userId || !projectId) return;

    const res = await fetch(
      `${apiUrl}/api/timebilling-invoices/project/${encodeURIComponent(
        userId
      )}/${encodeURIComponent(projectId)}`,
      { cache: 'no-store' }
    );
    const data = await res.json();
    setInvoices(Array.isArray(data) ? data : []);
  };

  const fetchProject = async () => {
    if (!userId || !projectId) return;

    const res = await fetch(
      `${apiUrl}/api/projects/${encodeURIComponent(userId)}/${encodeURIComponent(projectId)}`,
      { cache: 'no-store' }
    );
    const data = await res.json();

    setProject(data || null);
  };

  const fetchSettingsTaxLocations = async () => {
    if (!userId) return;
    try {
      const res = await fetch(`${apiUrl}/api/settings/${encodeURIComponent(userId)}`, {
        cache: 'no-store',
      });
      if (!res.ok) return;
      const data = await res.json();
      const normalized = Array.isArray(data.taxLocations)
        ? data.taxLocations.map((loc, index) => ({
            ...loc,
            id: loc?.id || `tax-${index}`,
          }))
        : [];
      setTaxLocations(normalized);
    } catch (err) {
      console.error('Failed to load tax settings for time billing:', err);
      setTaxLocations([]);
    }
  };

  const getEntryKey = (e) => e.sortKey || e.id;

  useEffect(() => {
    fetchEntries();
    fetchInvoices();
    fetchProject();
    fetchSettingsTaxLocations();
  }, [userId, projectId]);

  useEffect(() => {
    const refresh = () => {
      fetchEntries();
      fetchInvoices();
      fetchProject();
      fetchSettingsTaxLocations();
    };

    document.addEventListener('timebilling-updated', refresh);
    return () => document.removeEventListener('timebilling-updated', refresh);
  }, [projectId, userId]);

  /* =========================================================
     SORT HELPERS
  ========================================================= */
  const toggleSort = (setFn, key) => {
    setFn((prev) =>
      prev.key === key ? { key, dir: prev.dir === 'asc' ? 'desc' : 'asc' } : { key, dir: 'asc' }
    );
  };

  const sortArray = (arr, dir, accessor) =>
    [...arr].sort((a, b) => {
      const A = accessor(a);
      const B = accessor(b);

      if (A == null) return 1;
      if (B == null) return -1;

      if (typeof A === 'number') return dir === 'asc' ? A - B : B - A;
      return dir === 'asc'
        ? String(A).localeCompare(String(B))
        : String(B).localeCompare(String(A));
    });

  /* =========================================================
     COST / TAX HELPERS
  ========================================================= */
  const getRevenue = (e) => Number(e.rate || 0) * Number(e.billableHours || 0);

  const getCost = (e) => {
    const hours = Number(e.billableHours || 0);
    const rate = Number(e.rate || 0);
    return rate * hours;
  };

  const getEntrySubtotal = (e) => Number(e.billableHours || 0) * Number(e.rate || 0);

  // Legacy entries may not include `taxable`; treat missing as taxable.
  const isEntryTaxable = (e) => e?.taxable !== false;
  const getEntryTax = (e) => (isEntryTaxable(e) && taxRate ? getEntrySubtotal(e) * taxRate : 0);

  const getEntryTotalWithTax = (e) => getEntrySubtotal(e) + getEntryTax(e);

  const isEntryBilled = (e) => e.generatedForInvoice != null || e.invoiceNumber != null;

  /* =========================================================
     TIME ENTRIES
  ========================================================= */
  const timeEntries = useMemo(() => {
    const filtered =
      billableFilter === 'billable'
        ? entries.filter((e) => e.billable !== false)
        : billableFilter === 'nonbillable'
        ? entries.filter((e) => e.billable === false)
        : entries;

    return sortArray(filtered, entrySort.dir, (e) => {
      switch (entrySort.key) {
        case 'date':
          return new Date(e.date || e.createdAt);
        case 'rate':
          return Number(e.rate || 0);
        case 'employeeRate':
          return Number(e.employeeRate || 0);
        case 'billableHours':
          return Number(e.billableHours || 0);
        case 'cost':
          return getCost(e);
        case 'taxPreview':
          return getEntryTotalWithTax(e);
        case 'revenue':
          return getRevenue(e);
        default:
          return e[entrySort.key];
      }
    });
  }, [entries, entrySort, billableFilter]);

  const selectableEntries = useMemo(
    () => timeEntries.filter((e) => !isEntryBilled(e)),
    [timeEntries]
  );

  /* =========================================================
     INVOICES
  ========================================================= */
  const sortedInvoices = useMemo(() => {
    return sortArray(invoices, invoiceSort.dir, (inv) => {
      if (invoiceSort.key === 'createdAt') return String(inv.invoiceDate || inv.createdAt || '').slice(0, 10);
      return inv[invoiceSort.key];
    });
  }, [invoices, invoiceSort]);

  const isInvoicePaid = (inv) => Number(inv.remainingBalance) <= 0;
  const getInvoiceTotalDue = (inv) =>
    Math.max(0, Number(inv?.remainingBalance ?? inv?.totals?.total ?? 0));

  /* =========================================================
     SELECTION
  ========================================================= */
  const toggleRow = (entry) => {
    const key = getEntryKey(entry);
    if (!key) return;

    setSelected((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleSelectAll = () => {
    if (Object.keys(selected).length === selectableEntries.length) {
      setSelected({});
    } else {
      const next = {};
      selectableEntries.forEach((e) => {
        const key = getEntryKey(e);
        if (key) next[key] = true;
      });
      setSelected(next);
    }
  };

  const selectedEntries = timeEntries.filter((e) => {
    const key = getEntryKey(e);
    return key && selected[key];
  });

  /* =========================================================
     DELETE CONFIRM
  ========================================================= */
  async function confirmDelete() {
    if (!deleteContext) return;

    try {
      if (deleteContext.type === 'entries') {
        await Promise.all(
          deleteContext.payload.map((e) => {
            const key = getEntryKey(e);
            return fetch(
              `${apiUrl}/api/timebilling/${encodeURIComponent(userId)}/${encodeURIComponent(key)}`,
              { method: 'DELETE' }
            );
          })
        );

        setSelected({});
        fetchEntries();
      }

      if (deleteContext.type === 'invoice') {
        await fetch(
          `${apiUrl}/api/timebilling-invoices/${userId}/${deleteContext.payload.tbInvoiceId}`,
          { method: 'DELETE' }
        );
        fetchInvoices();
        fetchEntries();
      }
    } finally {
      setShowDeleteModal(false);
      setDeleteContext(null);
    }
  }

  /* =========================================================
     ACTIONS
  ========================================================= */
  const openEditForm = (entry) =>
    document.dispatchEvent(
      new CustomEvent('open-timebilling-form', {
        detail: { mode: 'edit', entry },
      })
    );

  const beginInvoiceCreation = () =>
    selectedEntries.length &&
    navigate(`/projects/${projectId}/timebilling-invoices/new`, {
      state: { entryKeys: selectedEntries.map((e) => e.sortKey) },
    });

  const addEntriesToInvoice = async (targetInvoiceId) => {
    try {
      setAddToInvoiceLoading(true);
      const res = await fetch(
        `${apiUrl}/api/timebilling-invoices/${encodeURIComponent(userId)}/${encodeURIComponent(targetInvoiceId)}/add-entries`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ entries: selectedEntries }),
        }
      );
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to add entries to invoice');
        return;
      }
      setSelected({});
      setShowAddToInvoiceModal(false);
      fetchEntries();
      fetchInvoices();
      navigate(`/projects/${projectId}/timebilling-invoices/${targetInvoiceId}`);
    } catch (err) {
      console.error('Failed to add entries to invoice:', err);
    } finally {
      setAddToInvoiceLoading(false);
    }
  };

  const openTBInvoice = (id) => navigate(`/projects/${projectId}/timebilling-invoices/${id}`);

  /* =========================================================
     STATS
  ========================================================= */
  const totalProjectHours = useMemo(
    () => entries.reduce((s, e) => s + Number(e.billableHours || 0), 0),
    [entries]
  );
  const unbilledBillableAmount = useMemo(
    () =>
      entries
        .filter((e) => e.generatedForInvoice == null && e.invoiceNumber == null && e.billable !== false)
        .reduce((s, e) => s + Number(e.billableHours || 0) * Number(e.rate || 0), 0),
    [entries]
  );
  const billedBillableAmount = useMemo(
    () =>
      entries
        .filter((e) => (e.generatedForInvoice != null || e.invoiceNumber != null) && e.billable !== false)
        .reduce((s, e) => s + Number(e.billableHours || 0) * Number(e.rate || 0), 0),
    [entries]
  );

  /* =========================================================
     RENDER
  ========================================================= */
  return (
    <div className="timebilling-table">
      {/* ===== STATS BAR ===== */}
      {entries.length > 0 && (
        <div className="tb-stats-strip">
          <div className="tb-stat-card">
            <span className="tb-stat-label">Total Hours</span>
            <span className="tb-stat-value">{totalProjectHours.toFixed(2)}</span>
          </div>
          <div className="tb-stat-card">
            <span className="tb-stat-label">Unbilled</span>
            <span className="tb-stat-value">${unbilledBillableAmount.toFixed(2)}</span>
          </div>
          <div className="tb-stat-card">
            <span className="tb-stat-label">Billed</span>
            <span className="tb-stat-value">${billedBillableAmount.toFixed(2)}</span>
          </div>
        </div>
      )}

      {/* ===== TOGGLE ===== */}
      <div className="tb-toggle">
        <button className={view === 'entries' ? 'active' : ''} onClick={() => setView('entries')}>
          Time Entries
        </button>
        <button className={view === 'invoices' ? 'active' : ''} onClick={() => setView('invoices')}>
          TB Invoices
        </button>
      </div>

      {/* ===== TIME ENTRIES ===== */}
      {view === 'entries' && (
        <>
          {selectedEntries.length > 0 && (canCreateTBInvoices || canEditEntries) && (
            <div className="tb-actions-row">
              {canCreateTBInvoices && (
                <button className="tb-btn tb-btn-primary" onClick={beginInvoiceCreation}>
                  Generate TB Invoice ({selectedEntries.length})
                </button>
              )}

              {canCreateTBInvoices && (
                <button
                  className="tb-btn tb-btn-secondary"
                  onClick={() => setShowAddToInvoiceModal(true)}
                >
                  Add to Existing Invoice
                </button>
              )}

              {canEditEntries && (
                <button
                  className="tb-btn tb-btn-danger"
                  onClick={() => {
                    setDeleteContext({ type: 'entries', payload: selectedEntries });
                    setShowDeleteModal(true);
                  }}
                >
                  Delete Selected
                </button>
              )}
            </div>
          )}

          <div className="tb-billable-filter">
            {[
              ['all', 'All'],
              ['billable', 'Billable'],
              ['nonbillable', 'Non-Billable'],
            ].map(([val, label]) => (
              <button
                key={val}
                className={billableFilter === val ? 'active' : ''}
                onClick={() => setBillableFilter(val)}
              >
                {label}
              </button>
            ))}
          </div>

          <table>
            <thead>
              <tr>
                {canSelectRows && (
                  <th>
                    <input
                      type="checkbox"
                      checked={
                        selectableEntries.length > 0 &&
                        selectedEntries.length === selectableEntries.length
                      }
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}

                {[
                  ['date', 'Date'],
                  ['description', 'Description'],
                  ['employee', 'Employee'],
                  ['activity', 'Activity'],
                  ['billable', 'Billable'],
                  ['rate', 'Client Rate'],
                  ['employeeRate', 'Employee Rate'],
                  ['billableHours', 'Hours'],
                  ['cost', 'Cost'],
                  ['taxPreview', 'Post Tax Cost'],
                ].map(([key, label]) => (
                  <th key={key} onClick={() => toggleSort(setEntrySort, key)}>
                    {label}
                    <SortArrow active={entrySort.key === key} dir={entrySort.dir} />
                  </th>
                ))}

                {canEditEntries && <th />}
              </tr>
            </thead>

            <tbody>
              {timeEntries.map((e) => {
                const billed = isEntryBilled(e);
                return (
                <tr
                  key={getEntryKey(e)}
                  style={e.billable === false || billed ? { opacity: 0.6 } : undefined}
                >
                  {canSelectRows && (
                    <td>
                      <input
                        type="checkbox"
                        checked={!billed && !!selected[getEntryKey(e)]}
                        disabled={billed}
                        title={billed ? 'Already invoiced' : undefined}
                        onChange={() => toggleRow(e)}
                      />
                    </td>
                  )}
                  <td>{e.date || e.createdAt?.split('T')[0]}</td>
                  <td>{e.description}</td>
                  <td>{e.employee}</td>
                  <td>{e.activity}</td>
                  <td>
                    {e.billable !== false ? (
                      <span className="tb-badge tb-badge-billable">Billable</span>
                    ) : (
                      <span className="tb-badge tb-badge-nonbillable">Non-Bill.</span>
                    )}
                    {billed && (
                      <span className="tb-badge tb-badge-billed" title="Already invoiced">
                        Billed
                      </span>
                    )}
                  </td>
                  <td>${Number(e.rate || 0).toFixed(2)}</td>
                  <td>${Number(e.employeeRate || 0).toFixed(2)}</td>
                  <td>{Number(e.billableHours || 0)}</td>
                  <td>${getCost(e).toFixed(2)}</td>
                  <td>{getEntryTax(e) > 0 ? '$' + getEntryTotalWithTax(e).toFixed(2) : '-'}</td>
                  {canEditEntries && (
                    <td>
                      <button onClick={() => openEditForm(e)}>
                        <Pencil1Icon />
                      </button>
                    </td>
                  )}
                </tr>
                );
              })}
            </tbody>
          </table>
        </>
      )}

      {/* ===== TB INVOICES ===== */}
      {view === 'invoices' && (
        <table>
          <thead>
            <tr>
              {[
                ['invoiceNumber', 'Invoice'],
                ['createdAt', 'Date'],
              ].map(([key, label]) => (
                <th key={key} onClick={() => toggleSort(setInvoiceSort, key)}>
                  {label}
                  <SortArrow active={invoiceSort.key === key} dir={invoiceSort.dir} />
                </th>
              ))}
              <th>Total Hours</th>
              <th>Total Due</th>
              <th>Status</th>
              {canCreateTBInvoices && <th />}
            </tr>
          </thead>

          <tbody>
            {sortedInvoices.map((inv) => {
              const paid = isInvoicePaid(inv);

              return (
                <tr
                  key={inv.tbInvoiceId}
                  className="tb-link-row"
                  onClick={() => openTBInvoice(inv.tbInvoiceId)}
                >
                  <td>{inv.invoiceNumber}</td>
                  <td>{inv.invoiceDate || inv.createdAt?.split('T')[0]}</td>
                  <td>
                    {(inv.groups || [])
                      .reduce(
                        (s, g) => s + (g.items || []).reduce((a, i) => a + Number(i.hours || 0), 0),
                        0
                      )
                      .toFixed(2)}
                  </td>
                  <td>${getInvoiceTotalDue(inv).toFixed(2)}</td>
                  <td>
                    {paid ? (
                      <span className="tb-status-paid">Paid</span>
                    ) : (
                      <span className="tb-status-unpaid">Unpaid</span>
                    )}
                  </td>
                  {canCreateTBInvoices && (
                    <td>
                      <button
                        className="tb-icon-btn danger"
                        disabled={paid}
                        title={paid ? 'Paid invoices cannot be deleted' : 'Delete invoice'}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDeleteContext({ type: 'invoice', payload: inv });
                          setShowDeleteModal(true);
                        }}
                        style={{ opacity: paid ? 0.3 : 1 }}
                      >
                        <TrashIcon />
                      </button>
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ===== ADD TO EXISTING INVOICE MODAL ===== */}
      {showAddToInvoiceModal && (
        <div className="rd-overlay" onClick={() => setShowAddToInvoiceModal(false)}>
          <div className="rd-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Add to Existing Invoice</h3>
            <p style={{ margin: '4px 0 12px', fontSize: '0.9rem', color: '#555' }}>
              Select an open invoice to add {selectedEntries.length} entr
              {selectedEntries.length > 1 ? 'ies' : 'y'} to:
            </p>

            {invoices.filter((inv) => Number(inv.remainingBalance) > 0).length === 0 ? (
              <p style={{ color: '#888', fontSize: '0.9rem' }}>No open invoices found.</p>
            ) : (
              <ul className="tb-invoice-selector">
                {invoices
                  .filter((inv) => Number(inv.remainingBalance) > 0)
                  .map((inv) => (
                    <li key={inv.tbInvoiceId}>
                      <button
                        className="tb-invoice-select-btn"
                        onClick={() => addEntriesToInvoice(inv.tbInvoiceId)}
                        disabled={addToInvoiceLoading}
                      >
                        <span className="tb-inv-sel-num">{inv.invoiceNumber}</span>
                        <span className="tb-inv-sel-date">{inv.createdAt?.slice(0, 10)}</span>
                        <span className="tb-inv-sel-due">
                          ${getInvoiceTotalDue(inv).toFixed(2)} due
                        </span>
                      </button>
                    </li>
                  ))}
              </ul>
            )}

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setShowAddToInvoiceModal(false)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ===== DELETE MODAL ===== */}
      {showDeleteModal && deleteContext && (
        <div className="rd-overlay" onClick={() => setShowDeleteModal(false)}>
          <div className="rd-modal" onClick={(e) => e.stopPropagation()}>
            <h3>Confirm Deletion</h3>

            {deleteContext.type === 'entries' && (
              <p>
                Delete <strong>{deleteContext.payload.length}</strong> time entr
                {deleteContext.payload.length > 1 ? 'ies' : 'y'}?
                <br />
                <br />
                This action cannot be undone.
              </p>
            )}

            {deleteContext.type === 'invoice' && (
              <p>
                Delete invoice <strong>{deleteContext.payload.invoiceNumber}</strong>?
                <br />
                <br />
                Time entries will be restored to unbilled.
              </p>
            )}

            <div className="rd-modal-actions">
              <button className="rd-cancel" onClick={() => setShowDeleteModal(false)}>
                Cancel
              </button>

              <button className="rd-save danger" onClick={confirmDelete}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
